import { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  Card, 
  CardContent,
  CircularProgress,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usersApi } from '@/api/users-api';
import { licensesApi } from '@/api/licenses-api';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalResellers: 0,
    totalCustomers: 0,
    totalSubscriptions: 0,
    totalLicenses: 0,
    activeSubscriptions: 0,
    expiringLicenses: 0,
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        // Different data loading based on user role
        if (user?.role === 'distributor') {
          const [resellers, customers, subscriptions, licenses] = await Promise.all([
            usersApi.getResellers(),
            usersApi.getCustomers(),
            licensesApi.getSubscriptions(),
            licensesApi.getLicenses(),
          ]);

          setStats({
            totalResellers: resellers.length,
            totalCustomers: customers.length,
            totalSubscriptions: subscriptions.length,
            totalLicenses: licenses.length,
            activeSubscriptions: subscriptions.filter(s => s.status === 'active').length,
            expiringLicenses: licenses.filter(l => {
              const expirationDate = new Date(l.expirationDate);
              const today = new Date();
              const thirtyDaysFromNow = new Date();
              thirtyDaysFromNow.setDate(today.getDate() + 30);
              return expirationDate <= thirtyDaysFromNow && expirationDate >= today;
            }).length,
          });
        } else if (user?.role === 'reseller') {
          const [customers, subscriptions, licenses] = await Promise.all([
            usersApi.getCustomers(),
            licensesApi.getSubscriptions(),
            licensesApi.getLicenses(),
          ]);

          setStats({
            totalResellers: 0, // Not relevant for resellers
            totalCustomers: customers.length,
            totalSubscriptions: subscriptions.length,
            totalLicenses: licenses.length,
            activeSubscriptions: subscriptions.filter(s => s.status === 'active').length,
            expiringLicenses: licenses.filter(l => {
              const expirationDate = new Date(l.expirationDate);
              const today = new Date();
              const thirtyDaysFromNow = new Date();
              thirtyDaysFromNow.setDate(today.getDate() + 30);
              return expirationDate <= thirtyDaysFromNow && expirationDate >= today;
            }).length,
          });
        } else if (user?.role === 'customer') {
          const [subscriptions, licenses] = await Promise.all([
            licensesApi.getSubscriptions(),
            licensesApi.getLicenses(),
          ]);

          setStats({
            totalResellers: 0, // Not relevant for customers
            totalCustomers: 0, // Not relevant for customers
            totalSubscriptions: subscriptions.length,
            totalLicenses: licenses.length,
            activeSubscriptions: subscriptions.filter(s => s.status === 'active').length,
            expiringLicenses: licenses.filter(l => {
              const expirationDate = new Date(l.expirationDate);
              const today = new Date();
              const thirtyDaysFromNow = new Date();
              thirtyDaysFromNow.setDate(today.getDate() + 30);
              return expirationDate <= thirtyDaysFromNow && expirationDate >= today;
            }).length,
          });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [user?.role]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
  <Typography variant="h4" gutterBottom>
    Dashboard
  </Typography>
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom>
      Welcome, {user?.email || 'Guest'}!
    </Typography>
    <Typography variant="body1" color="text.secondary">
      Role: {user?.role ? `${user.role.charAt(0).toUpperCase()}${user.role.slice(1)}` : 'Unknown'}
    </Typography>
  </Box>


      <Grid container spacing={3}>
        {user?.role === 'distributor' && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 3}}>
              <StatsCard
                title="Total Resellers"
                value={stats.totalResellers}
                action={() => navigate('/resellers')}
                actionText="View Resellers"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3}}>
              <StatsCard
                title="Total Customers"
                value={stats.totalCustomers}
                action={() => navigate('/customers')}
                actionText="View Customers"
              />
            </Grid>
          </>
        )}

        {user?.role === 'reseller' && (
          <Grid size={{ xs: 12, sm: 6, md: 3}}>
            <StatsCard
              title="Total Customers"
              value={stats.totalCustomers}
              action={() => navigate('/customers')}
              actionText="View Customers"
            />
          </Grid>
        )}

<Grid size={{ xs: 12, sm: 6, md: 3}}>
          <StatsCard
            title="Active Subscriptions"
            value={stats.activeSubscriptions}
            action={() => navigate('/subscriptions')}
            actionText="View Subscriptions"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3}}>
          <StatsCard
            title="Total Licenses"
            value={stats.totalLicenses}
            action={() => navigate('/licenses')}
            actionText="View Licenses"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3}}>
          <StatsCard
            title="Expiring Licenses"
            value={stats.expiringLicenses}
            action={() => navigate('/licenses')}
            actionText="View Licenses"
            highlight={stats.expiringLicenses > 0}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

interface StatsCardProps {
  title: string;
  value: number;
  action: () => void;
  actionText: string;
  highlight?: boolean;
}

function StatsCard({ title, value, action, actionText, highlight = false }: StatsCardProps) {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(highlight && {
          border: '1px solid',
          borderColor: 'warning.main',
        }),
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h5" component="div" sx={{ mb: 1 }}>
          {value}
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          {title}
        </Typography>
      </CardContent>
      <Box sx={{ p: 2, pt: 0 }}>
        <Button size="small" onClick={action}>
          {actionText}
        </Button>
      </Box>
    </Card>
  );
}
